import * as React from "react";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@material-ui/core";
import { API } from "aws-amplify";
import JsonRenderComponent from "../../../../utils/JsonRenderComponent";
import { Alert } from "@material-ui/lab";
import { LoadingSpinner } from "../../../../utils/LoadingSpinner";
import {
  expeditePacketsOrderDefault,
  IExpeditePacketsOrderRequest,
} from "./request/ExpeditePacketsOrderRequest";
import { Label } from "@aws-amplify/ui-react";

interface IProps {
  data?: any;
}

interface IState {
  errorMessage: string;
  isJsonInput: boolean;
  isLoading: boolean;
  jsonInput: string;
  request: IExpeditePacketsOrderRequest;
  response: any;
  billingDateDisplay: string;
  cutoffDateDisplay: string;
}

enum InputField {
  BILLING_DATE_IN_MIN,
  CONTRACT_ID,
  CUTOFF_DATE_IN_MIN,
  RECURRING,
}

class ExpeditePacketsOrderSection extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      errorMessage: "",
      isJsonInput: false,
      isLoading: false,
      jsonInput: expeditePacketsOrderDefault,
      request: {} as IExpeditePacketsOrderRequest,
      response: "",
      billingDateDisplay: "0",
      cutoffDateDisplay: "-1440",
    };

    this.toggleJsonInput = this.toggleJsonInput.bind(this);
    this.setRequest = this.setRequest.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleValidation = this.handleValidation.bind(this);
    this.handleRecurring = this.handleRecurring.bind(this);

    this.state.request.billingDateInMin = 0;
    this.state.request.cutoffDateInMin = -1440;
    this.state.request.recurring = false;
  }

  public render() {
    return (
      <Grid
        container
        direction="column"
        justify="center"
        alignItems="center"
        spacing={3}
      >
        <Grid item xs>
          <Label style={{ fontSize: "24px" }}>
            <a
              href="https://w.amazon.com/bin/view/Pharmacy/Public/Teams/APAQ/HowTos/ExpeditingPacketsorderusingAPAQPortal"
              target="_blank"
              rel="noopener noreferrer"
            >
              Click this link to view the wiki on "steps to expedite the packet
              order and place a recurring order."
            </a>
          </Label>
        </Grid>

        <Grid item xs>
          <FormGroup>
            <FormControlLabel
              control={<Switch onChange={this.toggleJsonInput} />}
              label="Toggle Json Input"
            />
          </FormGroup>
        </Grid>

        {!this.state.isJsonInput && (
          <Grid
            container
            direction="column"
            justify="center"
            alignItems="center"
            spacing={3}
          >
            <Grid item xs>
              <FormControl component="fieldset">
                <FormGroup style={{ width: 400 }}>
                  <FormControlLabel
                    control={
                      <Checkbox checked={this.state.request.recurring} />
                    }
                    label="Recurring Expedited Packet Order"
                    onChange={this.handleRecurring}
                  />
                </FormGroup>
              </FormControl>
            </Grid>
            <Grid item xs>
              <TextField
                style={{ width: 400 }}
                label="Contract Id (Required)"
                variant="outlined"
                value={this.state.request.contractId || ""}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  this.setRequestFields(InputField.CONTRACT_ID, event)
                }
              />
            </Grid>
            <Grid item xs>
              <FormControl variant="outlined" style={{ width: 400 }}>
                <Select
                  value={this.state.request.programName || ""}
                  onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                    const updatedRequest = { ...this.state.request };
                    updatedRequest.programName = event.target.value as string;
                    this.setState({ request: updatedRequest });
                  }}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Program Name (Required)</MenuItem>
                  <MenuItem value="AP_PILLPACK">AP_PILLPACK (Packets)</MenuItem>
                  <MenuItem value="AP_AUTO_REFILL">AP_AUTO_REFILL (AR Order)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs>
              <TextField
                style={{ width: 400 }}
                label="Billing Date Delay Time - unit is in minute"
                variant="outlined"
                value={this.state.billingDateDisplay}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  this.setRequestFields(InputField.BILLING_DATE_IN_MIN, event)
                }
              />
            </Grid>
            <Grid item xs>
              <TextField
                style={{ width: 400 }}
                label="Cutoff Date Delay Time - unit is in minute"
                variant="outlined"
                value={this.state.cutoffDateDisplay}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  this.setRequestFields(InputField.CUTOFF_DATE_IN_MIN, event)
                }
              />
            </Grid>
          </Grid>
        )}

        {this.state.isJsonInput && (
          <Grid item xs>
            <TextField
              multiline
              rows={8}
              label="Expedite a Packets Order Request"
              variant="filled"
              value={this.state.jsonInput}
              onChange={this.setRequest}
              style={{ width: 800 }}
            />
          </Grid>
        )}

        <Grid item xs>
          <Button
            variant="outlined"
            color="default"
            onClick={this.handleSubmit}
          >
            Submit
          </Button>
        </Grid>

        {this.state.isLoading && <LoadingSpinner />}

        {this.state.errorMessage && (
          <Alert variant="filled" severity="info">
            Error encountered: {this.state.errorMessage}
          </Alert>
        )}

        {this.state.response && (
          <div>
            <br />
            <h4>Status</h4>
            <p
              style={{
                border: "3px solid lightgray",
                padding: "5px",
                whiteSpace: "pre-wrap",
                wordBreak: "normal",
                wordWrap: "break-word",
              }}
            >
              <JsonRenderComponent
                jsonObject={this.state.response}
                printHtml={false}
              />
            </p>
          </div>
        )}
      </Grid>
    );
  }

  private toggleJsonInput() {
    this.setState({ isJsonInput: !this.state.isJsonInput });
  }

  private handleValidation() {
    let formIsValid = true;
    if (!this.state.request.contractId) {
      alert("Please enter the contract id");
      formIsValid = false;
    } else if (
      !this.state.request.contractId.match(
        "^amzn[1-9]+[0-9]*.([A-Za-z0-9]+.)+[A-Za-z0-9]*$"
      )
    ) {
      alert("Please enter the valid contract id.");
      formIsValid = false;
    }
    if (!this.state.request.programName) {
      alert("Please enter the programName");
      formIsValid = false;
    }
    if (this.state.billingDateDisplay === "" || this.state.billingDateDisplay === "-" ||
        this.state.cutoffDateDisplay === "" || this.state.cutoffDateDisplay === "-") {
      alert("Please enter valid numbers for billing and cutoff date fields.");
      formIsValid = false;
    } else if (this.state.request.billingDateInMin <= this.state.request.cutoffDateInMin) {
      alert("Billing date offset must be strictly greater than cutoff date offset.");
      formIsValid = false;
    }
    return formIsValid;
  }

  private handleRecurring(event: React.ChangeEvent<HTMLInputElement>) {
    const updatedRequest: IExpeditePacketsOrderRequest = this.state.request;
    if (event.target.checked) {
      updatedRequest.recurring = true;
    } else {
      updatedRequest.recurring = false;
    }
    this.setState({ request: updatedRequest });
    console.log(updatedRequest);
  }

  private setRequestFields(
    field: InputField,
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) {
    const updatedRequest: IExpeditePacketsOrderRequest = this.state.request;

    switch (field) {
      case InputField.CONTRACT_ID: {
        updatedRequest.contractId = event.target.value;
        this.setState({ request: updatedRequest });
        console.log(this.state.request);
        break;
      }
      case InputField.BILLING_DATE_IN_MIN: {
        const val = event.target.value;
        if (/^-?\d*$/.test(val)) {
          this.setState({ billingDateDisplay: val });
          if (val !== "" && val !== "-") {
            updatedRequest.billingDateInMin = Number(val);
            this.setState({ request: updatedRequest });
          }
        }
        break;
      }
      case InputField.CUTOFF_DATE_IN_MIN: {
        const val = event.target.value;
        if (/^-?\d*$/.test(val)) {
          this.setState({ cutoffDateDisplay: val });
          if (val !== "" && val !== "-") {
            updatedRequest.cutoffDateInMin = Number(val);
            this.setState({ request: updatedRequest });
          }
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  private setRequest(event: React.ChangeEvent<HTMLTextAreaElement>) {
    this.setState({ jsonInput: event.target.value });
  }

  private handleSubmit() {
    this.setState({ isLoading: true });
    this.setState({ errorMessage: "" });

    if (this.handleValidation()) {
      const config = {
        body: {
          billingDateInMin: this.state.request.billingDateInMin,
          contractId: this.state.request.contractId,
          cutoffDateInMin: this.state.request.cutoffDateInMin,
          programName: this.state.request.programName,
          recurring: this.state.request.recurring,
        },
        headers: {},
      };
      this.callExpeditePacketsOrder(config);
    } else {
      this.setState({ isLoading: false });
    }
  }

  private callExpeditePacketsOrder(config: any) {
    const apiName = "ApaqPortalAPI";
    const path = "/pharmacy-customer/expedite-packets-order";

    console.log(config);
    API.post(apiName, path, config)
      .then((getResponse: any) => {
        console.log(getResponse);
        this.setState({ response: getResponse, errorMessage: "", isLoading: false });
      })
      .catch((error) => {
        console.log(error);
        this.setState({ response: "", errorMessage: error.response.data.message, isLoading: false });
      });
  }
}

export default ExpeditePacketsOrderSection;
